/**
 * BoothFlipGuestbook — Guestbook flow inside Booth OS.
 *
 * A slower, more intimate flow than Photo / Video. Reuses the same
 * `/api/events/{slug}/guestbook/entries` endpoint the host's own
 * guestbook page uses, so entries land in the memory book + recap
 * exactly like online guestbook submissions.
 *
 * Tone: parchment page on walnut desk. Italic prompt. Short name.
 * Single-tap "Leave the message" — no signature drawing in v1 (that
 * lives in the existing guestbook surface and adds complexity that
 * doesn't fit a touch-kiosk on event night).
 */
import React, { useState } from 'react';
import { SERIF, Eyebrow, Heading, Lede, GoldPill, FlowFrame } from './primitives';
import { sendOrQueue, newDedupeId } from '../../lib/booth-os/uploadQueue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_NAME = 60;
const MAX_MESSAGE = 600;

const PROMPTS = [
  'What do you want them to remember?',
  'What did tonight feel like?',
  'A line for the morning ✦',
  'A small wish for the road ahead',
];

const BoothFlipGuestbook = ({ theme, event, onBack, onComplete }) => {
  // 'compose' | 'saving' | 'saved'
  const [stage, setStage] = useState('compose');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  // Deterministic-ish prompt — same prompt for the duration of one
  // visit so the guest doesn't see it shift while they think.
  const [promptIndex] = useState(() => Math.floor(Math.random() * PROMPTS.length));

  const canPreserve = message.trim().length >= 2;

  const preserve = async () => {
    if (!canPreserve) return;
    setError(null);
    setStage('saving');
    try {
      // The endpoint accepts either slug or event_id as the path key.
      const key = event.slug || event.event_id;
      const id = newDedupeId();
      const r = await sendOrQueue({
        kind: 'json',
        url: `${API}/events/${key}/guestbook/entries`,
        jsonBody: {
          guest_name: name.trim() || 'A guest',
          entry_type: 'typed',
          message_text: message.trim().slice(0, MAX_MESSAGE),
          font_family: 'serif',
          dedupe_id: id,
        },
        dedupeId: id,
      });
      setStage('saved');
      // Slightly longer dwell — guestbook submissions feel like
      // closing a book; they deserve a beat before the room moves on.
      setTimeout(() => onComplete({
        deferred: !r.viaNetwork,
        // iter210 ✦ — pass the dedupe_id through as contribution_id
        // so the Personal Memory Page can resolve this guestbook
        // entry. The page reads it via the lookup-by-dedupe_id
        // fallback inside the personal_memory router.
        contribution_id: id,
      }), 1800);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[BoothFlipGuestbook] preserve failed', e?.response?.data || e?.message);
      setError('The page didn’t turn — try once more.');
      setStage('compose');
    }
  };

  return (
    <FlowFrame theme={theme} eyebrow="✦ ADD TO THE GUESTBOOK" onBack={onBack} testId="booth-flip-guestbook">
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(20px, 3vw, 36px) 0',
      }}>
        {stage === 'saved' ? (
          <div data-testid="booth-guestbook-saved" style={{ textAlign: 'center', maxWidth: 520 }}>
            <Eyebrow theme={theme}>✦ PRESERVED</Eyebrow>
            <Heading theme={theme}>Your words are in the book ✦</Heading>
            <Lede theme={theme}>
              They\u2019ll be there in the morning. The next page is yours, if you\u2019d like it.
            </Lede>
          </div>
        ) : (
          <div style={{
            width: 'min(720px, 92vw)',
            background: theme.polaroidFrame,
            borderRadius: 6,
            padding: 'clamp(30px, 4vw, 50px) clamp(28px, 4vw, 56px)',
            boxShadow: '0 28px 70px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.55)',
            position: 'relative',
            // Subtle binding shadow on the left edge — page-from-a-book feel.
            backgroundImage: `linear-gradient(90deg, rgba(0,0,0,0.10) 0%, transparent 4%, transparent 96%, rgba(0,0,0,0.06) 100%)`,
          }}>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase',
              color: '#7A5A2E', marginBottom: 10, textAlign: 'center',
            }}>
              ✦ THE GUESTBOOK · {event.event_name || 'TONIGHT'} ✦
            </div>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 'clamp(16px, 1.6vw, 19px)',
              color: '#5A3F1F', textAlign: 'center',
              margin: '0 0 22px', letterSpacing: '0.01em',
            }}>
              {PROMPTS[promptIndex]}
            </div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, MAX_NAME))}
              placeholder="Your name (optional)"
              data-testid="booth-guestbook-name"
              style={{
                width: '100%', padding: '10px 4px',
                background: 'transparent', border: 'none',
                borderBottom: '1px dashed rgba(122,90,46,0.45)',
                color: '#3F2A14', fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 16, outline: 'none', marginBottom: 16,
              }}
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
              placeholder="Write a few lines…"
              rows={6}
              data-testid="booth-guestbook-message"
              style={{
                width: '100%', padding: '12px 4px',
                background: 'transparent', border: 'none',
                color: '#3F2A14', fontFamily: SERIF,
                fontSize: 'clamp(16px, 1.5vw, 19px)',
                lineHeight: 1.65, outline: 'none', resize: 'none',
                // Ruled-page underline rhythm — subtle, parchment-aged ink.
                backgroundImage: 'repeating-linear-gradient(0deg, transparent 0, transparent 30px, rgba(122,90,46,0.18) 30px, rgba(122,90,46,0.18) 31px)',
              }}
              autoFocus
            />
            <div style={{
              marginTop: 6, display: 'flex', justifyContent: 'space-between',
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 12, color: '#7A5A2E',
            }}>
              <span>{error || (stage === 'saving' ? 'The book is closing\u2026' : '')}</span>
              <span>{message.length} / {MAX_MESSAGE}</span>
            </div>
            <div style={{
              marginTop: 26, display: 'flex', gap: 12,
              justifyContent: 'flex-end', flexWrap: 'wrap',
            }}>
              <GoldPill
                theme={theme}
                onClick={preserve}
                disabled={!canPreserve || stage === 'saving'}
                testId="booth-guestbook-preserve"
                large
              >
                {stage === 'saving' ? 'Closing the page\u2026' : 'Leave the message ✦'}
              </GoldPill>
            </div>
          </div>
        )}
      </div>
    </FlowFrame>
  );
};

export default BoothFlipGuestbook;
